import * as yup from 'yup';
import type { StoreFormData } from '../../../../types/features/storeDetails/storeForm';
import { DEFAULT_COUNTER_SCANNING_METHODS } from '../utils/storeDetailsStoreFormUtils';

export const storeDetailsStoreSchema: yup.ObjectSchema<StoreFormData> = yup.object({
    name: yup.string().required('Store name is required').trim(),
    address_line1: yup.string().required('Address is required').trim(),
    address_line2: yup.string().trim(),
    city: yup.string().required('City is required').trim(),
    state: yup.string().required('State is required').trim(),
    postal_code: yup.string().required('Postal code is required').trim(),
    country: yup.string().required('Country is required').trim(),
    latitude: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
                return null;
            }
            return value;
        })
        .min(-90, 'Latitude must be between -90 and 90')
        .max(90, 'Latitude must be between -90 and 90'),
    longitude: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
                return null;
            }
            return value;
        })
        .min(-180, 'Longitude must be between -180 and 180')
        .max(180, 'Longitude must be between -180 and 180'),
    status: yup.string().required('Status is required'),
    brand: yup.string().trim(),
    pos_type: yup.string().required('POS type is required').trim(),
    cash_register_enabled: yup.boolean().required(),
    direction: yup
        .mixed<StoreFormData['direction']>()
        .oneOf(['backwards', 'forwards'], 'Counting direction must be forwards or backwards')
        .required('Counting direction is required'),
    store_closing_time: yup
        .string()
        .required('Store closing time is required')
        .matches(/^([01]\d|2[0-3]):[0-5]\d$/, 'Store closing time must be a valid time'),
    backstock_audit_frequency_seconds: yup
        .number()
        .nullable()
        .transform((value, originalValue) => {
            if (originalValue === '' || originalValue === null || originalValue === undefined) {
                return null;
            }
            return value;
        })
        .integer('Backstock audit frequency must be a whole number')
        .positive('Backstock audit frequency must be greater than zero')
        .typeError('Backstock audit frequency must be a number'),
    scratcher_reconciliation_cutoff_offset_minutes: yup
        .number()
        .required('Scratcher cutoff offset is required')
        .integer('Scratcher cutoff offset must be a whole number')
        .typeError('Scratcher cutoff offset must be a number'),
    pos_integration_interest: yup.boolean().required(),
    lotto_authorized: yup.boolean().required(),
    enable_audit_confirmation_summary: yup.boolean().required(),
    require_audit_mismatch_note: yup.boolean().required(),
    logo_image_url: yup.string().trim(),
    lottery_reseller_id: yup.string().trim(),
    pos_payout_mapping: yup
        .object({
            financial_event: yup.array().of(yup.string().trim().required()).required(),
            transaction_line: yup.array().of(yup.string().trim().required()).required(),
            lotto_merch_description: yup.array().of(yup.string().trim().required()).required(),
        })
        .required(),
    scanning_methods: yup
        .object({
            counter: yup
                .array()
                .of(
                    yup
                        .mixed<StoreFormData['scanning_methods']['counter'][number]>()
                        .oneOf(DEFAULT_COUNTER_SCANNING_METHODS)
                        .required()
                )
                .min(1, 'Select at least one counter audit method')
                .required('Select at least one counter audit method'),
        })
        .required(),
}) as yup.ObjectSchema<StoreFormData>;
